import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CardSystemEditor from "../elementEditors/CardSystemEditor";
import { getSegmentDefinition, SEGMENT_TYPES } from "../segmentRegistry";
import { getLandingPreviewDevice } from "../previewDevices";
import { resolveSiteExperienceDefaults } from "../../landingEditor/siteExperienceDefaults";
import {
  ColorSetting,
  EditorGroup,
  NumberSetting,
  PageEditorShell,
  PagePreviewTarget,
  PageHiddenTarget,
  PageEditorContextIntro,
  PageEditorBackButton,
  SelectSetting,
  TextSetting,
  ToggleSetting,
} from "./PageEditorControls";

const PREVIEW_PRODUCT_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="960" height="640" viewBox="0 0 960 640">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="#e9ecf4"/>
        <stop offset="1" stop-color="#cfd6e6"/>
      </linearGradient>
    </defs>
    <rect width="960" height="640" rx="36" fill="url(#bg)"/>
    <circle cx="760" cy="130" r="92" fill="#ffffff" opacity=".65"/>
    <rect x="285" y="118" width="390" height="404" rx="54" fill="#ffffff"/>
    <rect x="352" y="172" width="256" height="214" rx="26" fill="#6a5ae0" opacity=".88"/>
    <rect x="374" y="414" width="212" height="34" rx="17" fill="#19242f" opacity=".78"/>
    <rect x="414" y="468" width="132" height="18" rx="9" fill="#19242f" opacity=".34"/>
  </svg>
`)}`;

function normalizedCardImageHeight(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 210;
  return Math.min(420, Math.max(90, parsed));
}

function previewCardImagePosition(position) {
  if (position === "left") return "left center";
  if (position === "right") return "right center";
  return "center top";
}

function pageValue(form) {
  return form?.catalog?.productsPage || {};
}

function cardValue(form) {
  return form?.catalog?.cards || {};
}

function headerBackground(page, theme) {
  if (page.headerBackgroundColor) return page.headerBackgroundColor;
  return `radial-gradient(circle at 15% 20%, ${theme?.primaryColor || "#E44E31"} 0%, transparent 34%), linear-gradient(125deg, ${theme?.secondaryColor || "#494A98"}, #17182d)`;
}

const TARGETS = Object.freeze({
  page: { label: "Página", hint: "Fondo, superficies y estados" },
  eyebrow: { label: "Texto superior", hint: "Copy del encabezado" },
  title: { label: "Título", hint: "Título general y variantes" },
  subtitle: { label: "Subtítulo", hint: "Descripción del catálogo" },
  results: { label: "Resultados", hint: "Singular y plural" },
  search: { label: "Buscador", hint: "Visibilidad y placeholder" },
  filters: { label: "Filtros", hint: "Visibilidad y etiquetas" },
  sort: { label: "Orden", hint: "Orden predeterminado" },
  pagination: { label: "Paginación", hint: "Modo y cantidad por página" },
  cards: { label: "Cards del catálogo", hint: "Sistema compartido" },
});

function targetMeta(targetId) {
  return TARGETS[targetId] || TARGETS.page;
}


function ProductsPreviewTarget({ targetId, activeTarget, onSelect, label, children, sx = {} }) {
  return (
    <PagePreviewTarget
      targetId={targetId}
      activeTarget={activeTarget}
      onSelect={onSelect}
      label={label || targetMeta(targetId).label}
      sx={sx}
    >
      {children}
    </PagePreviewTarget>
  );
}

function sortPreviewLabel(page) {
  const by = page.sortBy || "createdAt";
  const dir = page.sortDir || "desc";
  const byLabel = by === "price" ? "Precio" : by === "name" ? "Nombre" : "Más recientes";
  return `${byLabel} · ${dir === "asc" ? "Ascendente" : "Descendente"}`;
}

function productsPreviewTitle(page, navigation, catalogContext) {
  if (page.title) return page.title;
  if (catalogContext === "product") return page.productsTitle || "Productos";
  if (catalogContext === "service") return page.servicesTitle || "Servicios";
  if (catalogContext === "hybrid") return page.hybridTitle || "Productos + servicio";
  return navigation?.productsLabel || "Productos y servicios";
}

function ProductsPagePreview({ form, activeTarget, onSelectTarget, catalogContext, previewState, device }) {
  const page = pageValue(form);
  const cards = cardValue(form);
  const theme = form?.theme || {};
  const previewDevice = getLandingPreviewDevice(device);
  const isDesktop = previewDevice.id === "desktop";
  const isMobile = previewDevice.id === "mobile";
  const filtersVisible = page.showFilters !== false;
  const compactFilters = filtersVisible && !isDesktop;
  const textColor = page.textColor || theme.textColor || "#1A1A2E";
  const mutedText = page.mutedTextColor || theme.mutedTextColor || "#5B5D73";
  const surface = page.surfaceColor || theme.surfaceColor || "#FFFFFF";
  const background = page.backgroundColor || theme.backgroundColor || "#EEF0F5";
  const headerText = page.headerTextColor || "#FFFFFF";
  const cardBackground = cards.backgroundColor || surface;
  const cardImagePosition = ["top", "left", "right"].includes(cards.imagePosition) ? cards.imagePosition : "top";
  const cardImageSide = cardImagePosition === "left" || cardImagePosition === "right";
  const showCardImage = cards.showImage !== false;
  const cardImageHorizontal = showCardImage && cardImageSide && isDesktop;
  const cardImageHeight = normalizedCardImageHeight(cards.imageHeight);
  const collapsedImageHeight = Math.max(120, Math.round(cardImageHeight * 0.76));
  const previewImageHeight = cardImageSide
    ? (isDesktop ? cardImageHeight : collapsedImageHeight)
    : (isMobile ? collapsedImageHeight : cardImageHeight);
  const cardImageFit = cards.imageFit === "contain" ? "contain" : "cover";
  const cardImageRight = cardImagePosition === "right";
  const previewResultsText = previewState === "loading"
    ? (page.loadingText ?? "Cargando catálogo…")
    : previewState === "empty"
      ? `0 ${page.resultPlural ?? "resultados"}`
      : `12 ${page.resultPlural || "resultados"}`;
  const emptyTitle = page.emptyTitle ?? "No encontramos resultados";
  const emptyText = page.emptyText ?? "Probá con otra búsqueda o quitá los filtros activos.";
  const emptyResetLabel = page.emptyResetLabel ?? "Ver todo el catálogo";

  return (
    <Box
      sx={{
        minHeight: 680,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflowX: "hidden",
        bgcolor: background,
        color: textColor,
        position: "relative",
      }}
      onClick={() => onSelectTarget("page")}
    >
      <Button
        size="small"
        variant={activeTarget === "page" ? "contained" : "outlined"}
        startIcon={<TuneRoundedIcon />}
        onClick={(event) => {
          event.stopPropagation();
          onSelectTarget("page");
        }}
        sx={{ position: "absolute", zIndex: 8, top: 10, right: 10, bgcolor: activeTarget === "page" ? undefined : "rgba(255,255,255,.94)" }}
      >
        Página
      </Button>

      <Box sx={{ px: isDesktop ? 5 : 2, pt: isDesktop ? 9 : 7, pb: 5, color: headerText, background: headerBackground(page, theme) }}>
        <Stack spacing={1.1} sx={{ minWidth: 0 }}>
          <ProductsPreviewTarget targetId="eyebrow" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" variant="overline" sx={{ color: "inherit", opacity: page.eyebrow ? 0.72 : 0.48, fontStyle: page.eyebrow ? "normal" : "italic" }}>
              {page.eyebrow || "Texto superior"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="title" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Typography data-preview-passive="true" variant="h3" sx={{ color: "inherit", fontWeight: 900, overflowWrap: "anywhere" }}>
              {productsPreviewTitle(page, form?.navigation, catalogContext)}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="subtitle" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Typography data-preview-passive="true" sx={{ color: "inherit", opacity: page.subtitle ? 0.82 : 0.48, fontStyle: page.subtitle ? "normal" : "italic", overflowWrap: "anywhere" }}>
              {page.subtitle || "Subtítulo opcional"}
            </Typography>
          </ProductsPreviewTarget>

          <ProductsPreviewTarget targetId="results" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Typography data-preview-passive="true" sx={{ color: "inherit", opacity: 0.72 }}>
              {previewResultsText}
            </Typography>
          </ProductsPreviewTarget>
        </Stack>
      </Box>

      <Box sx={{ px: isDesktop ? 4 : 1.5, pb: 5, minWidth: 0 }}>
        {(page.showSearch !== false || compactFilters) ? (
          <Box
            sx={{
              mt: -2.5,
              p: 1.4,
              bgcolor: surface,
              borderRadius: 2.5,
              boxShadow: "0 14px 36px rgba(25,36,47,.14)",
              display: "flex",
              gap: 1.25,
              alignItems: "center",
              minWidth: 0,
            }}
          >
            {page.showSearch !== false ? (
              <ProductsPreviewTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  data-preview-passive="true"
                  fullWidth
                  size="small"
                  disabled
                  placeholder={page.searchPlaceholder || "Buscar por nombre, servicio, código, categoría o subcategoría"}
                  InputProps={{ startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: mutedText }} /> }}
                />
              </ProductsPreviewTarget>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0 }} />
            )}

            {compactFilters ? (
              <ProductsPreviewTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ flex: "0 0 auto" }}>
                <Button
                  data-preview-passive="true"
                  tabIndex={-1}
                  variant="outlined"
                  startIcon={<FilterListRoundedIcon />}
                  sx={{ minHeight: 40, minWidth: isMobile ? 42 : 118, px: isMobile ? 1 : 1.5, pointerEvents: "none" }}
                >
                  {isMobile ? null : (page.filtersLabel || "Filtros")}
                </Button>
              </ProductsPreviewTarget>
            ) : null}
          </Box>
        ) : null}

        {page.showSearch === false ? (
          <Box sx={{ mt: compactFilters ? 1 : -2.5 }}>
            <PageHiddenTarget targetId="search" activeTarget={activeTarget} onSelect={onSelectTarget} label="Buscador" />
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end" sx={{ mt: 2.4, mb: -1, minWidth: 0 }}>
          <ProductsPreviewTarget targetId="sort" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ minWidth: 0 }}>
            <Chip data-preview-passive="true" size="small" icon={<TuneRoundedIcon />} label={`Orden · ${sortPreviewLabel(page)}`} />
          </ProductsPreviewTarget>
          <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget}>
            <Chip data-preview-passive="true" size="small" variant="outlined" label={`${Number(page.pageSize) || 24} por página`} />
          </ProductsPreviewTarget>
        </Stack>

        {!filtersVisible ? (
          <Box sx={{ mt: 3 }}>
            <PageHiddenTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} label="Filtros" />
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 3,
            display: "grid",
            gridTemplateColumns: filtersVisible && isDesktop ? "220px minmax(0,1fr)" : "minmax(0,1fr)",
            gap: 2.5,
            minWidth: 0,
          }}
        >
          {filtersVisible && isDesktop ? (
            <ProductsPreviewTarget targetId="filters" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ alignSelf: "start", minWidth: 0 }}>
              <Box data-preview-passive="true" sx={{ bgcolor: surface, borderRadius: 2.5, p: 2, minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <FilterListRoundedIcon fontSize="small" />
                  <Typography fontWeight={900}>{page.filtersLabel || "Filtros"}</Typography>
                </Stack>
                <Stack spacing={1.2}>
                  {page.showTypeFilter !== false ? <Chip label={page.typeFilterLabel || "Tipo"} variant="outlined" /> : null}
                  {page.showCategoryFilter !== false ? <Chip label={page.categoryFilterLabel || "Categoría"} variant="outlined" /> : null}
                  {page.showSubcategoryFilter !== false ? <Chip label={page.subcategoryFilterLabel || "Subcategoría"} variant="outlined" /> : null}
                </Stack>
              </Box>
            </ProductsPreviewTarget>
          ) : null}

          <Box sx={{ minWidth: 0 }}>
            {previewState === "loading" ? (
              <Box
                role="status"
                aria-label={page.loadingText ?? "Cargando catálogo…"}
                sx={{ minHeight: 360, display: "grid", placeItems: "center", minWidth: 0 }}
              >
                <CircularProgress />
              </Box>
            ) : previewState === "empty" ? (
              <Paper
                variant="outlined"
                sx={{ p: isMobile ? 3 : 5, textAlign: "center", bgcolor: surface, color: textColor, borderRadius: 2.5, minWidth: 0 }}
              >
                <Typography variant="h4" sx={{ color: textColor }}>
                  {emptyTitle}
                </Typography>
                <Typography sx={{ mt: 1, color: mutedText }}>
                  {emptyText}
                </Typography>
                <Button
                  data-preview-passive="true"
                  tabIndex={-1}
                  variant="contained"
                  sx={{ mt: 3, pointerEvents: "none" }}
                >
                  {emptyResetLabel}
                </Button>
              </Paper>
            ) : (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${isDesktop ? 3 : 2}, minmax(0,1fr))`,
                    gap: `${Number(cards.gap) || 18}px`,
                    minWidth: 0,
                  }}
                >
                  {["Producto destacado", "Servicio principal", "Nueva colección", "Más vendido", "Producto especial", "Servicio premium"].map((name, index) => (
                    <ProductsPreviewTarget
                      key={name}
                      targetId="cards"
                      activeTarget={activeTarget}
                      onSelect={onSelectTarget}
                      label="Cards del catálogo"
                      sx={{ minWidth: 0 }}
                    >
                      <Box
                        data-preview-passive="true"
                        sx={{
                          display: "grid",
                          gridTemplateColumns: cardImageHorizontal
                            ? (cardImageRight ? "62% minmax(0,38%)" : "38% minmax(0,62%)")
                            : "minmax(0,1fr)",
                          gridTemplateRows: cardImageHorizontal
                            ? "minmax(0,1fr)"
                            : (showCardImage ? "auto minmax(0,1fr)" : "minmax(0,1fr)"),
                          minHeight: cardImageHorizontal ? cardImageHeight : undefined,
                          minWidth: 0,
                          overflow: "hidden",
                          borderRadius: `${Number(cards.radius) || 16}px`,
                          bgcolor: cardBackground,
                          border: cards.borderColor ? `1px solid ${cards.borderColor}` : "1px solid rgba(25,36,47,.08)",
                        }}
                      >
                        {showCardImage ? (
                          <Box
                            sx={{
                              minWidth: 0,
                              overflow: "hidden",
                              bgcolor: cardImageFit === "contain" ? "#fff" : "rgba(25,36,47,.08)",
                              gridColumn: cardImageHorizontal ? (cardImageRight ? 2 : 1) : 1,
                              gridRow: cardImageHorizontal ? 1 : 1,
                              minHeight: cardImageHorizontal ? cardImageHeight : 0,
                            }}
                          >
                            <Box
                              component="img"
                              src={PREVIEW_PRODUCT_IMAGE}
                              alt=""
                              aria-hidden="true"
                              sx={{
                                display: "block",
                                width: "100%",
                                height: cardImageHorizontal ? "100%" : previewImageHeight,
                                minHeight: cardImageHorizontal ? cardImageHeight : undefined,
                                objectFit: cardImageFit,
                                objectPosition: previewCardImagePosition(cardImagePosition),
                              }}
                            />
                          </Box>
                        ) : null}
                        <Box
                          sx={{
                            p: 1.5,
                            minWidth: 0,
                            gridColumn: cardImageHorizontal ? (cardImageRight ? 1 : 2) : 1,
                            gridRow: cardImageHorizontal ? 1 : (showCardImage ? 2 : 1),
                            alignSelf: "stretch",
                          }}
                        >
                          {cards.showCategory !== false ? (
                            <Typography variant="caption" sx={{ color: cards.categoryColor || theme.primaryColor || "#F35A3A", fontWeight: 800 }}>
                              Categoría
                            </Typography>
                          ) : null}
                          {cards.showTitle !== false ? (
                            <Typography sx={{ mt: 0.3, fontWeight: 900, color: cards.titleColor || textColor, overflowWrap: "anywhere" }}>
                              {name}
                            </Typography>
                          ) : null}
                          {cards.showPrice !== false ? (
                            <Typography sx={{ mt: 1, fontWeight: 900, color: cards.priceColor || theme.secondaryColor || "#19242F" }}>
                              $ {index % 2 ? "25.000" : "18.500"}
                            </Typography>
                          ) : null}
                          {cards.showButton !== false ? (
                            <Button tabIndex={-1} size="small" variant="outlined" fullWidth sx={{ mt: 1.2, pointerEvents: "none" }}>
                              {cards.ctaLabel || "Ver detalle"}
                            </Button>
                          ) : null}
                        </Box>
                      </Box>
                    </ProductsPreviewTarget>
                  ))}
                </Box>

                <ProductsPreviewTarget targetId="pagination" activeTarget={activeTarget} onSelect={onSelectTarget} sx={{ mt: 3, minWidth: 0 }}>
                  <Box data-preview-passive="true" sx={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
                    {page.paginationMode === "loadMore" ? (
                      <Button tabIndex={-1} variant="outlined" sx={{ pointerEvents: "none" }}>
                        {page.loadMoreLabel || "Cargar más"}
                      </Button>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" justifyContent="center">
                        <Button tabIndex={-1} size="small" variant="outlined" sx={{ pointerEvents: "none" }}>Anterior</Button>
                        <Typography variant="body2" sx={{ color: mutedText }}>Página 1 de 4</Typography>
                        <Button tabIndex={-1} size="small" variant="outlined" sx={{ pointerEvents: "none" }}>Siguiente</Button>
                      </Stack>
                    )}
                  </Box>
                </ProductsPreviewTarget>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function PageCopyEditor({ targetId, page, set, catalogContext, onCatalogContextChange }) {
  if (targetId === "eyebrow") {
    return <TextSetting label="Texto superior" value={page.eyebrow} onChange={(value) => set("eyebrow", value)} />;
  }
  if (targetId === "subtitle") {
    return <TextSetting label="Subtítulo" value={page.subtitle} onChange={(value) => set("subtitle", value)} multiline />;
  }
  if (targetId === "results") {
    return (
      <EditorGroup title="Cómo mostramos la cantidad" description="Preview: 1 resultado / 12 resultados">
        <TextSetting label="Singular" value={page.resultSingular} onChange={(value) => set("resultSingular", value)} />
        <TextSetting label="Plural" value={page.resultPlural} onChange={(value) => set("resultPlural", value)} />
      </EditorGroup>
    );
  }
  return (
    <Stack spacing={1.5}>
      <TextSetting label="Título general" value={page.title} onChange={(value) => set("title", value)} helperText="Vacío = usar el nombre del catálogo de la navegación." />
      <EditorGroup title="Título según el tipo de catálogo" description="Se usa cuando el título general está vacío y corresponde distinguir el catálogo.">
        <TextSetting label="Productos" value={page.productsTitle} onChange={(value) => set("productsTitle", value)} helperText="Vacío vuelve al título automático de la experiencia activa." />
        <TextSetting label="Servicios" value={page.servicesTitle} onChange={(value) => set("servicesTitle", value)} />
        <TextSetting label="Productos + servicio" value={page.hybridTitle} onChange={(value) => set("hybridTitle", value)} helperText="Vacío vuelve al título automático de la experiencia activa." />
        <Divider />
        <SelectSetting
          label="Simular catálogo en preview"
          value={catalogContext}
          onChange={onCatalogContextChange}
          options={[
            { value: "none", label: "Sin filtro" },
            { value: "product", label: "Productos" },
            { value: "service", label: "Servicios" },
            { value: "hybrid", label: "Productos + servicio" },
          ]}
          helperText="Sólo cambia esta vista previa; el tipo real depende del filtro que use el visitante."
        />
        {page.title ? (
          <Typography variant="caption" sx={{ display: "block", color: "rgba(25,36,47,.64)" }}>
            El título general reemplaza las variantes por tipo.
          </Typography>
        ) : null}
      </EditorGroup>
    </Stack>
  );
}

function SearchEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar buscador" checked={page.showSearch} onChange={(value) => set("showSearch", value)} />
      <TextSetting label="Texto dentro del buscador" value={page.searchPlaceholder} onChange={(value) => set("searchPlaceholder", value)} helperText="Vacío vuelve al texto automático de la experiencia activa." />
    </Stack>
  );
}

function FiltersEditor({ page, set }) {
  const filtersVisible = page.showFilters !== false;

  return (
    <Stack spacing={1.4}>
      <ToggleSetting label="Mostrar filtros" checked={page.showFilters} onChange={(value) => set("showFilters", value)} />
      {filtersVisible ? (
        <>
          <EditorGroup title="Qué filtros mostrar">
            <ToggleSetting label="Tipo" checked={page.showTypeFilter} onChange={(value) => set("showTypeFilter", value)} />
            <ToggleSetting label="Categoría" checked={page.showCategoryFilter} onChange={(value) => set("showCategoryFilter", value)} />
            <ToggleSetting label="Subcategoría" checked={page.showSubcategoryFilter} onChange={(value) => set("showSubcategoryFilter", value)} />
          </EditorGroup>
          <EditorGroup title="Textos de filtros">
            <TextSetting label="Título" value={page.filtersLabel} onChange={(value) => set("filtersLabel", value)} />
            <TextSetting label="Tipo" value={page.typeFilterLabel} onChange={(value) => set("typeFilterLabel", value)} />
            <TextSetting label="Categoría" value={page.categoryFilterLabel} onChange={(value) => set("categoryFilterLabel", value)} />
            <TextSetting label="Subcategoría" value={page.subcategoryFilterLabel} onChange={(value) => set("subcategoryFilterLabel", value)} />
            <Divider />
            <TextSetting label="Todos los tipos" value={page.allTypesLabel} onChange={(value) => set("allTypesLabel", value)} helperText="Vacío vuelve al texto automático de la experiencia activa." />
            <TextSetting label="Todas las categorías" value={page.allCategoriesLabel} onChange={(value) => set("allCategoriesLabel", value)} />
            <TextSetting label="Todas las subcategorías" value={page.allSubcategoriesLabel} onChange={(value) => set("allSubcategoriesLabel", value)} />
            <TextSetting label="Limpiar filtros" value={page.resetLabel} onChange={(value) => set("resetLabel", value)} />
            <TextSetting label="Aplicar filtros" value={page.applyFiltersLabel} onChange={(value) => set("applyFiltersLabel", value)} />
          </EditorGroup>
        </>
      ) : (
        <Paper
          variant="outlined"
          role="status"
          aria-live="polite"
          sx={{ p: 1.4, borderRadius: 2.3, bgcolor: "rgba(35,111,231,.035)" }}
        >
          <Typography variant="body2" sx={{ color: "rgba(25,36,47,.72)" }}>
            Los filtros están ocultos en la página. Tus opciones se conservan y volverán a estar disponibles al activarlos.
          </Typography>
        </Paper>
      )}
    </Stack>
  );
}

function SortEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.64)" }}>
        Este es el orden inicial con el que se abre el catálogo.
      </Typography>
      <SelectSetting
        label="Ordenar por"
        value={page.sortBy || "createdAt"}
        onChange={(value) => set("sortBy", value)}
        options={[
          { value: "createdAt", label: "Más recientes" },
          { value: "price", label: "Precio" },
          { value: "name", label: "Nombre" },
        ]}
      />
      <SelectSetting
        label="Dirección"
        value={page.sortDir || "desc"}
        onChange={(value) => set("sortDir", value)}
        options={[
          { value: "desc", label: "Descendente" },
          { value: "asc", label: "Ascendente" },
        ]}
      />
    </Stack>
  );
}

function PaginationEditor({ page, set }) {
  return (
    <Stack spacing={1.4}>
      <SelectSetting
        label="Comportamiento"
        value={page.paginationMode || "all"}
        onChange={(value) => set("paginationMode", value)}
        options={[
          { value: "all", label: "Páginas" },
          { value: "loadMore", label: "Cargar más" },
        ]}
      />
      <NumberSetting
        label="Resultados por página"
        value={page.pageSize}
        min={12}
        max={60}
        onChange={(value) => set("pageSize", value)}
        helperText="Elegí entre 12 y 60 resultados por página."
      />
      {page.paginationMode === "loadMore" ? (
        <TextSetting label="Botón Cargar más" value={page.loadMoreLabel} onChange={(value) => set("loadMoreLabel", value)} />
      ) : null}
    </Stack>
  );
}

function PageSurfaceEditor({ page, set, previewState, onPreviewStateChange }) {
  return (
    <Stack spacing={1.5}>
      <EditorGroup title="Apariencia de la página" description="Las cards se editan tocando una card de ejemplo.">
        <ColorSetting label="Fondo" value={page.backgroundColor} onChange={(value) => set("backgroundColor", value)} />
        <ColorSetting label="Superficies" value={page.surfaceColor} onChange={(value) => set("surfaceColor", value)} />
        <ColorSetting label="Texto" value={page.textColor} onChange={(value) => set("textColor", value)} />
        <ColorSetting label="Texto secundario" value={page.mutedTextColor} onChange={(value) => set("mutedTextColor", value)} />
        <ColorSetting label="Fondo del encabezado" value={page.headerBackgroundColor} onChange={(value) => set("headerBackgroundColor", value)} />
        <ColorSetting label="Texto del encabezado" value={page.headerTextColor} onChange={(value) => set("headerTextColor", value)} />
      </EditorGroup>
      <EditorGroup title="Mensajes y estados" description="Sólo aparecen cuando el catálogo carga o no encuentra resultados.">
        <Box>
          <Typography variant="caption" sx={{ display: "block", mb: 0.8, fontWeight: 850, color: "rgba(25,36,47,.62)" }}>
            Estado del preview
          </Typography>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={previewState}
            onChange={(_, value) => value && onPreviewStateChange?.(value)}
            aria-label="Estado del preview del catálogo"
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                flex: "1 1 105px",
                minWidth: 0,
                px: 1,
                textTransform: "none",
                fontWeight: 850,
              },
            }}
          >
            <ToggleButton value="results">Con resultados</ToggleButton>
            <ToggleButton value="loading">Cargando</ToggleButton>
            <ToggleButton value="empty">Sin resultados</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ display: "block", mt: 0.7, color: "rgba(25,36,47,.56)" }}>
            Sólo simula esta vista previa; no modifica la configuración del sitio.
          </Typography>
        </Box>
        <TextSetting label="Cargando" value={page.loadingText} onChange={(value) => set("loadingText", value)} helperText="Vacío vuelve al texto automático de la experiencia activa." />
        <TextSetting label="Título sin resultados" value={page.emptyTitle} onChange={(value) => set("emptyTitle", value)} helperText="Vacío vuelve al texto automático de la experiencia activa." />
        <TextSetting label="Texto sin resultados" value={page.emptyText} onChange={(value) => set("emptyText", value)} multiline helperText="Vacío vuelve al texto automático de la experiencia activa." />
        <TextSetting label="Botón sin resultados" value={page.emptyResetLabel} onChange={(value) => set("emptyResetLabel", value)} helperText="Vacío vuelve al texto automático de la experiencia activa." />
      </EditorGroup>
    </Stack>
  );
}

function catalogCardsDescriptor() {
  const definition = getSegmentDefinition(SEGMENT_TYPES.FEATURED);
  const fields = (definition?.properties || []).filter((property) => String(property?.path || "").startsWith("catalog.cards."));
  return {
    kind: "card-system",
    label: "Cards del catálogo",
    fieldPrefix: "catalog.cards.",
    fields,
  };
}

export default function ProductsPageEditor({
  form,
  device,
  gastronomicMenuEnabled = false,
  productsEnabled = true,
  saving = false,
  onCommit,
  onUploadImage,
  onRemoveImage,
}) {
  const page = pageValue(form);
  const previewForm = resolveSiteExperienceDefaults(form, {
    gastronomicMenuEnabled,
    productsEnabled,
  });
  const [selectedTarget, setSelectedTarget] = React.useState("page");
  const [catalogContext, setCatalogContext] = React.useState("none");
  const [previewState, setPreviewState] = React.useState("results");
  const set = React.useCallback((key, value) => onCommit?.(`catalog.productsPage.${key}`, value), [onCommit]);
  const cardsDescriptor = React.useMemo(catalogCardsDescriptor, []);
  const selectedMeta = targetMeta(selectedTarget);

  let editor = null;
  if (["eyebrow", "title", "subtitle", "results"].includes(selectedTarget)) {
    editor = (
      <PageCopyEditor
        targetId={selectedTarget}
        page={page}
        set={set}
        catalogContext={catalogContext}
        onCatalogContextChange={setCatalogContext}
      />
    );
  } else if (selectedTarget === "search") {
    editor = <SearchEditor page={page} set={set} />;
  } else if (selectedTarget === "filters") {
    editor = <FiltersEditor page={page} set={set} />;
  } else if (selectedTarget === "sort") {
    editor = <SortEditor page={page} set={set} />;
  } else if (selectedTarget === "pagination") {
    editor = <PaginationEditor page={page} set={set} />;
  } else if (selectedTarget === "cards") {
    editor = (
      <CardSystemEditor
        descriptor={cardsDescriptor}
        form={form}
        disabled={saving}
        onCommit={onCommit}
        onUploadImage={onUploadImage}
        onRemoveImage={onRemoveImage}
      />
    );
  } else {
    editor = <PageSurfaceEditor page={page} set={set} previewState={previewState} onPreviewStateChange={setPreviewState} />;
  }

  return (
    <PageEditorShell
      title={`Catálogo › ${selectedMeta.label}`}
      description={selectedMeta.hint}
      device={device}
      saving={saving}
      preview={(
        <ProductsPagePreview
          form={previewForm}
          activeTarget={selectedTarget}
          onSelectTarget={setSelectedTarget}
          catalogContext={catalogContext}
          previewState={previewState}
          device={device}
        />
      )}
    >
      {selectedTarget !== "page" ? (
        <PageEditorBackButton
          onClick={() => setSelectedTarget("page")}
          startIcon={<ArrowBackRoundedIcon />}
        />
      ) : (
        <PageEditorContextIntro>
          Tocá cualquier elemento del preview para editar sólo ese componente.
        </PageEditorContextIntro>
      )}
      {editor}
    </PageEditorShell>
  );
}
